import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../WizardLayout';
import Stepper from '../Stepper';
import { LuLink, LuUpload, LuSparkles, LuShoppingCart, LuBriefcase, LuSmartphone, LuPlug, LuPenTool, LuCheck, LuKeyRound, LuClipboardList, LuActivity } from 'react-icons/lu';
import { OnboardingModal, WizardSelect, FileDropzone, FileList, OnboardingAlert } from '../onboarding';
import {
  verifyConnectorConnection,
  fetchConnectorProjects,
  uploadFiles,
  createRequirementsLite,
  createSampleProject
} from '../../../services/wizardApi';
import { toast } from 'react-toast';
import axiosInstance from '../../../api/axiosInstance';
import ConfirmIntegrationModal from '../ConfirmIntegrationModal';
import useProjectSocket from '../../../hooks/useProjectSocket';
import '../../../styles/wizard.css';
import { INTENT_TYPES, STEP_STATUS } from '../../../Config/Onboarding/constant';
import { useAddProjectKey } from '../../../hooks/API/projects.api';
import { getKeySuggestions, getProjectNameFromStorage, isValidUploadKey } from '../../../utils/projectKey.util';
import ProjectKeyField from '../../projects/ProjectKeyField';

// Source types for unified output
const SOURCE_TYPES = {
  CONNECTOR: 'CONNECTOR',
  UPLOAD: 'UPLOAD',
  REQUIREMENTS_LITE: 'REQUIREMENTS_LITE',
  SAMPLE: 'SAMPLE'
};

const getWsMessageText = (msg) => {
  const payload = msg?.message;
  if (typeof payload === 'string') return payload.trim();
  if (payload && typeof payload.message === 'string') return payload.message.trim();
  return '';
};

// Path options
const PATH_OPTIONS = {
  CONNECTOR: 'connector',
  UPLOAD: 'upload',
  CREATE: 'create'
};

// Template options for Create in Robitest
const TEMPLATE_OPTIONS = [
  { id: 'ecommerce', name: 'E-Commerce', icon: LuShoppingCart, description: 'Login, Cart, Checkout flows' },
  { id: 'saas', name: 'SaaS Application', icon: LuBriefcase, description: 'User management, Settings, Dashboard' },
  { id: 'mobile', name: 'Mobile App', icon: LuSmartphone, description: 'Onboarding, Navigation, Forms' },
  { id: 'api', name: 'API Testing', icon: LuPlug, description: 'REST API endpoints, Auth, CRUD' },
  { id: 'custom', name: 'Custom', icon: LuPenTool, description: 'Start from scratch' }
];



const Step4TestSource = () => {
  const navigate = useNavigate();
  const { 
    engine, 
    data, 
    state,
    intent, 
    testSource, 
    setTestSource, 
    nextStep, 
    previousStep, 
    setProject,
    setStepStatus,
    setStepCreatedEntityId,
    sessionId ,
    markStepCompleted,
    goToStep
  } = useOnboarding();
  
  // Step status
  const stepId = 4;
  const stepState = engine.steps[stepId];
  const projectData = data.project;
  const isCreating = stepState?.status === STEP_STATUS.IN_PROGRESS;
  // const isCreating = true // for testing 
  const isCompleted = stepState?.status === STEP_STATUS.COMPLETED;
  
  // Determine default path based on intent
  const getDefaultPath = () => {
    if (intent === INTENT_TYPES.SAMPLE_PROJECT) return PATH_OPTIONS.CREATE;
    if (intent === INTENT_TYPES.JIRA_REQUIREMENTS) return PATH_OPTIONS.CONNECTOR;
    if (intent === INTENT_TYPES.MANUAL_TO_AUTOMATION) return PATH_OPTIONS.UPLOAD;
    return PATH_OPTIONS.CONNECTOR; // Default
  };

  const [selectedPath, setSelectedPath] = useState(getDefaultPath());
  const [isLoading, setIsLoading] = useState(false);
  const projectNameFromStorage = getProjectNameFromStorage();

  // WebSocket integration for progress tracking
  const { 
    status: wsStatus, 
    messages: wsMessages, 
    progress: wsProgress, 
    connectWebSocket, 
    sendEvent,
    clearMessages,
  } = useProjectSocket(projectData?.project_id);

  // Loader and progress state
  const [showProgressLoader, setShowProgressLoader] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Starting test generation...');
  const [currentProgress, setCurrentProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(''); // 'epic' or 'sync'
  const [shouldNavigateAfterComplete, setShouldNavigateAfterComplete] = useState(false);
  const wsMessagesContainerRef = useRef(null);

  const displayWsMessages = wsMessages
    .map((msg) => getWsMessageText(msg))
    .filter(Boolean);

  // Connector state
  const [connectorType, setConnectorType] = useState(testSource.pmTool || ''); // 'jira' or 'project_pulse'
  const [connectorAccessKey, setConnectorAccessKey] = useState(testSource.pmAccessKey || '');
  const [connectorApiUrl, setConnectorApiUrl] = useState(testSource.pmApiUrl || '');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraDomain, setJiraDomain] = useState('');
  const [ppUsername, setPpUsername] = useState('');
  const [connectorVerified, setConnectorVerified] = useState(
    Boolean(testSource.linkedProjectId || testSource.selectedPMProject)
  );
  const [connectorProjects, setConnectorProjects] = useState(
    testSource.connector?.meta?.projects || []
  );
  const [selectedProjects, setSelectedProjects] = useState(() => {
    const id = testSource.selectedPMProject || testSource.linkedProjectId;
    return id ? [id] : [];
  });
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [connectorRequirementFiles, setConnectorRequirementFiles] = useState([]);
  const connectorRequirementFileRef = useRef(null);
  const [uploadProjectKeyError, setUploadProjectKeyError] = useState('');
  const [isProjectKeySaved, setIsProjectKeySaved] = useState(false);

  
  const [uploadProjectKey, setUploadProjectKey] = useState('');
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isAnalyzingRequirement, setIsAnalyzingRequirement] = useState(false);
  const [isProjectLinked, setIsProjectLinked] = useState(
    Boolean(testSource.linkedProjectId || testSource.selectedPMProject)
  );
  const [hasGeneratedRequirements, setHasGeneratedRequirements] = useState(
    Boolean(testSource.requirementFileUploaded)
  );
  const [isGeneratingRequirements, setIsGeneratingRequirements] = useState(false);
  const hasCalledSync = useRef(false);

  const getLinkedProject = () => {
    const linkedId = selectedProjects[0] || testSource.linkedProjectId || testSource.selectedPMProject;
    if (linkedId && connectorProjects.length > 0) {
      const found = connectorProjects.find(
        (p) => String(p.id) === String(linkedId)
      );
      if (found) return found;
    }

    const persisted = testSource.connector?.meta?.projects?.[0];
    if (persisted) return persisted;

    if (testSource.linkedProjectId || testSource.linkedProjectKey) {
      return {
        id: testSource.linkedProjectId || testSource.selectedPMProject,
        key: testSource.linkedProjectKey,
        name: testSource.linkedProjectName || testSource.linkedProjectKey || 'Linked project',
      };
    }

    return null;
  };

  const renderLinkedProjectCard = () => {
    const linkedProject = getLinkedProject();
    if (!linkedProject) return null;

    return (
      <div className="ob-tile">
        <span className="ob-choice__icon ob-choice__icon--success" aria-hidden="true">
          <LuCheck size={18} />
        </span>
        <div className="ob-tile__body">
          <p className="ob-tile__title">
            Project linked to {connectorType === 'jira' ? 'JIRA' : 'WorkXiq'}
          </p>
          <p className="ob-tile__sub">
            {linkedProject.key || linkedProject.identifier} — {linkedProject.name}
          </p>
        </div>
        <span className="ob-badge ob-badge--success">Linked</span>
      </div>
    );
  };

  // Upload state
  const [uploadedFiles, setUploadedFiles] = useState({
    requirements: [], // PDFs
    manualTests: [], // Excel/CSV
    uiImages: [] // Images for visual reference
  });
  const [uploadFlowRequirementFiles, setUploadFlowRequirementFiles] = useState([]);
  const requirementFileRef = useRef(null);
  const uploadFlowRequirementFileRef = useRef(null);
  const manualTestFileRef = useRef(null);
  const uiImageFileRef = useRef(null);

  // Create in Robitest state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [requirementsLiteData, setRequirementsLiteData] = useState({
    epics: [],
    stories: []
  });
  const [inlineRequirement, setInlineRequirement] = useState({
    title: '',
    description: '',
    type: 'story' // 'epic' or 'story'
  });

  // Reset path-specific state when path changes
  useEffect(() => {
    // Don't reset on initial mount
    const defaultPath = getDefaultPath();
    if (selectedPath !== defaultPath) {
      // User manually changed path, could reset here if needed
    }
  }, [selectedPath]);

  useEffect(() => {
    if (projectData?.project_id) {
      connectWebSocket();
    }
  }, [projectData?.project_id]);

  // Restore linked connector state when returning to this step
  useEffect(() => {
    const linkedId = testSource.linkedProjectId || testSource.selectedPMProject;
    if (!linkedId) return;

    setIsProjectLinked(true);
    setConnectorVerified(true);
    setSelectedProjects([linkedId]);

    if (testSource.connector?.meta?.projects?.length) {
      setConnectorProjects(testSource.connector.meta.projects);
    }

    const refreshProjects = async () => {
      if (!testSource.pmTool || testSource.connector?.meta?.projects?.length) return;
      try {
        const source = testSource.pmTool === 'jira' ? 1 : 2;
        const projectsResponse = await axiosInstance.get(`/jira_data/get_projects?source=${source}`);
        if (projectsResponse.projects?.length) {
          setConnectorProjects(projectsResponse.projects);
        }
      } catch (err) {
        console.warn('[Step 4] Could not refresh connector projects on restore:', err.message);
      }
    };

    refreshProjects();
  }, []);

  // Connect WebSocket on component load
  useEffect(() => {
    if (projectData?.project_id && wsStatus === 'connected') {
    sendEvent("sync_epics", {
      project_id: projectData.project_id,
      progress: 0,
      message: "Starting test generation..."
    });
  }
  }, [projectData?.project_id]);

// for testing
  // useEffect(()=>{
  //     getProjects();
  // },[])

  // Monitor WebSocket progress for loader
  useEffect(() => {
    if (wsProgress) {
      setCurrentProgress(wsProgress);
      
      // Update message based on progress
      if (wsProgress < 50) {
        setProgressMessage(
          currentPhase === 'sync' 
            ? 'Syncing JIRA epics and stories...' 
            : 'Analyzing requirements and generating epics...'
        );
      } else if (wsProgress < 100) {
        setProgressMessage(
          currentPhase === 'sync' 
            ? 'Finalizing sync (almost done)...' 
            : 'Finalizing epics (almost done)...'
        );
      }
      
      // When progress reaches 100%, complete flow
      if (wsProgress === 100) {
        if (shouldNavigateAfterComplete) {
          setTimeout(() => {
            setShowProgressLoader(false);
            setShouldNavigateAfterComplete(false);
            markStepCompleted(stepId);
            setStepStatus(5, STEP_STATUS.IN_PROGRESS);
            nextStep();
            navigate('/app/onboarding?step=5',{replace:true});
          }, 1000);
        } else if (currentPhase === 'sync' && selectedPath === PATH_OPTIONS.CONNECTOR) {
          setTimeout(() => {
            setShowProgressLoader(false);
            setIsGeneratingRequirements(false);
            setHasGeneratedRequirements(true);
            hasCalledSync.current = false;
            toast.success('Requirements generated and synced successfully!');
          }, 1000);
        }
      }
    }
  }, [wsProgress, shouldNavigateAfterComplete, currentPhase, selectedPath, navigate, markStepCompleted, nextStep, setStepStatus, stepId]);

  // Handle WebSocket messages for additional context
  useEffect(() => {
    const texts = wsMessages
      .map((msg) => getWsMessageText(msg))
      .filter(Boolean);
    if (texts.length > 0) {
      setProgressMessage(texts[texts.length - 1]);
    }
  }, [wsMessages]);

  // Keep the live message log scrolled to the latest update
  useEffect(() => {
    if (!showProgressLoader) return;
    const container = wsMessagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [wsMessages, showProgressLoader]);

  // Handle phase transition from epic to sync (for connector + file upload case)
  useEffect(() => {
    if (wsProgress === 100 && currentPhase === 'epic' && showProgressLoader && !hasCalledSync.current) {
          hasCalledSync.current = true; // ✅ prevent duplicate calls
      console.log('[Step 4 Phase Transition] Epic generation complete at 100%, transitioning to sync phase');
      
      // Small delay to ensure UI update
      setTimeout(async () => {
        try {

          // Call sync JIRA epics API
          const syncResponse = await axiosInstance.post(
            `/epics/sync-jira-epics/${projectData.project_id}`,
            {}
          );

          if (syncResponse.status_code === 200) {
          setCurrentPhase('sync');
          setCurrentProgress(0);
          setProgressMessage('Syncing JIRA epics and stories...');

            console.log('[Step 4 Phase Transition] Sync API called successfully');
          } else {
            throw new Error('Failed to call sync API');
          }
        } catch (syncErr) {
          console.error('[Step 4 Phase Transition] Sync API error:', syncErr);
          setShowProgressLoader(false);
          setShouldNavigateAfterComplete(false);
          setIsGeneratingRequirements(false);
          hasCalledSync.current = false;
          toast.error('Failed to sync epics');
        }
      }, 500);
    }
  }, [wsProgress, currentPhase, showProgressLoader, projectData.project_id]);

  // Task 1: Check if step 4 is completed - redirect to last in-progress step
  useEffect(() => {
  const step4State = engine.steps[4];
  
  if (step4State?.status === STEP_STATUS.COMPLETED) {
    const inProgressSteps = Object.keys(engine.steps)
      .filter(stepId => engine.steps[stepId]?.status === STEP_STATUS.IN_PROGRESS)
      .map(Number)
      .sort((a, b) => b - a);
    
    if (inProgressSteps.length > 0) {
      const lastInProgressStep = inProgressSteps[0];
      // Only redirect if last in-progress step is HIGHER than 4
      // AND engine.flow.currentStep agrees we should be there
      // Don't redirect if user intentionally navigated back (currentStep <= 4)
      if (lastInProgressStep && lastInProgressStep !== 4 && engine.flow.currentStep > 4) {
        navigate(`/app/onboarding?step=${lastInProgressStep}`);
      }
    }
  }
}, [engine.steps, navigate, engine.flow.currentStep]);

  // ============================================
  // CONNECTOR PATH HANDLERS
  // ============================================

  const handleConnectorTypeChange = (type) => {
    setConnectorType(type);
    setConnectorAccessKey('');
    setConnectorApiUrl(type === 'project_pulse' ? 'https://workxiq.example.com/api' : '');
    setConnectorProjects([]);
    setSelectedProjects([]);
    setConnectorVerified(false);
    setIsProjectLinked(false);
    setHasGeneratedRequirements(false);
    setConnectorRequirementFiles([]);
  };
  // for testing 

  // const getProjects = async ()=>{
  //               setConnectorVerified(true);

  //             const projectsResponse = await axiosInstance.get('/jira_data/get_projects?source=2');
  //         if (projectsResponse.projects && Array.isArray(projectsResponse.projects)) {
  //           // setConnectorVerified(true);
  //           setConnectorProjects(projectsResponse.projects);
  //           // toast.success('JIRA connected successfully!');
  //         }
  //       }
  

  const handleVerifyConnection = async () => {
    if (!connectorAccessKey) {
      toast.error('Please enter access key');
      return;
    }

    // Validate JIRA fields
    if (connectorType === 'jira' && (!jiraEmail.trim() || !jiraDomain.trim())) {
      toast.error('Please fill in all JIRA fields');
      return;
    }

    // Validate WorkXiq fields
    if (connectorType === 'project_pulse' && !ppUsername.trim()) {
      toast.error('Please fill in all WorkXiq fields');
      return;
    }

    setIsLoadingProjects(true);
    try {
      // API Integration for JIRA
      if (connectorType === 'jira') {
        const credResponse = await axiosInstance.post('/jira_datasave_credentials', {
          email: jiraEmail.trim(),
          domain: jiraDomain.trim(),
          api_token: connectorAccessKey.trim(),
          is_connected : true
        });

        if (credResponse.status_code === 200 || credResponse.status_code === 201) {
          const projectsResponse = await axiosInstance.get('/jira_data/get_projects?source=1');
          
          if (projectsResponse.projects && Array.isArray(projectsResponse.projects)) {
            setConnectorVerified(true);
            setConnectorProjects(projectsResponse.projects);
            toast.success('JIRA connected successfully!');
          }
        }
      }
      // API Integration for WorkXiq
      else if (connectorType === 'project_pulse') {
        const credResponse = await axiosInstance.post('/pulse/add_project_pulse_cred', {
          username: ppUsername.trim(),
          app_token: connectorAccessKey.trim(),
          is_connected: true
        });

        if (credResponse.status_code === 200 || credResponse.status_code === 201) {
          const projectsResponse = await axiosInstance.get('/jira_data/get_projects?source=2');
          
          if (projectsResponse.projects && Array.isArray(projectsResponse.projects)) {
            setConnectorVerified(true);
            setConnectorProjects(projectsResponse.projects);
            // Success toast comes from axios interceptor (API response message)
          }
        }
      }
      else {
        // Fallback to existing API call
        const response = await verifyConnectorConnection({
          system: connectorType,
          accessKey: connectorAccessKey,
          apiUrl: connectorApiUrl
        });

        if (response.success) {
          setConnectorVerified(true);
          setConnectorProjects(response.data.projects || []);
          toast.success('Connection verified!');
        } else {
          throw new Error('Verification failed');
        }
      }
    } catch (error) {
      console.error('Connection error:', error);
      toast.error(error.response?.data?.message || 'Connection failed. Please check your credentials.');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const handleProjectSelection = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      }
      return [...prev, projectId];
    });
  };

  const handleConnectorRequirementFileChange = (files) => {
    if (files && files.length > 0) {
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isPdf = file.type === 'application/pdf';
        const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        
        if (isPdf || isDocx) {
          validFiles.push({
            id: `${Date.now()}_${i}`,
            file: file,
            name: file.name,
            size: file.size,
            type: isPdf ? 'pdf' : 'docx'
          });
        } else {
          toast.error(`${file.name} is not a valid file type. Please upload PDF or DOCX files only.`);
        }
      }
      
      if (validFiles.length > 0) {
        setConnectorRequirementFiles(prev => [...prev, ...validFiles]);
        setHasGeneratedRequirements(false);
        toast.success(`${validFiles.length} file(s) added successfully`);
      }
    }
  };

  const handleRemoveConnectorRequirementFile = (fileId) => {
    setConnectorRequirementFiles(prev => {
      const next = prev.filter(f => f.id !== fileId);
      if (next.length === 0) {
        setHasGeneratedRequirements(false);
      }
      return next;
    });
  };

  const handleUploadFlowRequirementFileChange = (files) => {
    if (files && files.length > 0) {
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isPdf = file.type === 'application/pdf';
        const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        
        if (isPdf || isDocx) {
          validFiles.push({
            id: `${Date.now()}_${i}`,
            file: file,
            name: file.name,
            size: file.size,
            type: isPdf ? 'pdf' : 'docx'
          });
        } else {
          toast.error(`${file.name} is not a valid file type. Please upload PDF or DOCX files only.`);
        }
      }
      
      if (validFiles.length > 0) {
        setUploadFlowRequirementFiles(prev => [...prev, ...validFiles]);
        toast.success(`${validFiles.length} file(s) added successfully`);
      }
    }
  };

  const handleRemoveUploadFlowRequirementFile = (fileId) => {
    setUploadFlowRequirementFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const callGenerateEpicTasks = async (file, projectKey, email) => {
    try {
      setIsAnalyzingRequirement(true);
      const formData = new FormData();
      formData.append('files', file);
      formData.append('project_key', projectKey);
      formData.append('email', email);

      const response = await axiosInstance.post(
        '/ai_test_case/generate-epic-tasks/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (response.status_code === 200 || response.status_code === 201 || response) {
        toast.success('Requirements analyzed successfully!');
        await callSyncJiraEpics(projectData.project_id);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error generating epic tasks:', error);
      toast.error(error.response?.data?.message || 'Failed to analyze requirements');
      return false;
    } finally {
      setIsAnalyzingRequirement(false);
    }
  };

  const callSyncJiraEpics = async (projectId) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await axiosInstance.post(
        `${API_BASE_URL}/epics/sync-jira-epics/${projectId}`,
        {}
      );

      if (response.status_code === 200 || response.status_code === 201) {
        toast.success('Epics synced successfully!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error syncing epics:', error);
      toast.error(error.response?.data?.message || 'Failed to sync epics');
      return false;
    }
  }

  // ============================================
  // UPLOAD PATH HANDLERS
  // ============================================

  const handleFileUpload = async (fileType, files) => {
    if (!files || files.length === 0) return;

    setIsLoading(true);
    try {
      const response = await uploadFiles({
        fileType,
        files: Array.from(files)
      });

      if (response.success) {
        setUploadedFiles(prev => ({
          ...prev,
          [fileType]: [...prev[fileType], ...response.data.file_ids]
        }));
        toast.success(`${files.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFile = (fileType, fileId) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: prev[fileType].filter(id => id !== fileId)
    }));
  };

  // ============================================
  // CREATE IN ROBITEST HANDLERS
  // ============================================

  const handleAddRequirement = () => {
    if (!inlineRequirement.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    const newItem = {
      id: `temp_${Date.now()}`,
      title: inlineRequirement.title,
      description: inlineRequirement.description,
      type: inlineRequirement.type
    };

    if (inlineRequirement.type === 'epic') {
      setRequirementsLiteData(prev => ({
        ...prev,
        epics: [...prev.epics, newItem]
      }));
    } else {
      setRequirementsLiteData(prev => ({
        ...prev,
        stories: [...prev.stories, newItem]
      }));
    }

    // Reset form
    setInlineRequirement({
      title: '',
      description: '',
      type: 'story'
    });

    toast.success(`${inlineRequirement.type === 'epic' ? 'Epic' : 'Story'} added`);
  };

  const handleRemoveRequirement = (type, id) => {
    if (type === 'epic') {
      setRequirementsLiteData(prev => ({
        ...prev,
        epics: prev.epics.filter(e => e.id !== id)
      }));
    } else {
      setRequirementsLiteData(prev => ({
        ...prev,
        stories: prev.stories.filter(s => s.id !== id)
      }));
    }
  };

  // ============================================
  // VALIDATION & CONTINUE
  // ============================================

  const canContinue = () => {
    // Sample project always allows continue
    if (intent === INTENT_TYPES.SAMPLE_PROJECT) return true;

    switch (selectedPath) {
      case PATH_OPTIONS.CONNECTOR:
        if (!isProjectLinked) return false;
        if (connectorRequirementFiles.length > 0 && !hasGeneratedRequirements) return false;
        return true;
      
      case PATH_OPTIONS.UPLOAD:
        // Upload flow now requires at least one file to be uploaded
        return uploadFlowRequirementFiles.length > 0 && isProjectKeySaved;
      
      case PATH_OPTIONS.CREATE:
        return selectedTemplate && (
          requirementsLiteData.epics.length > 0 || 
          requirementsLiteData.stories.length > 0
        );
      
      default:
        return false;
    }
  };


  const handleLinkProjectClick = () => {
    if (!connectorVerified || !selectedProjects[0]) {
      toast.error('Please connect and select a project');
      return;
    }
    if (isProjectLinked) {
      return;
    }
    setShowConfirmModal(true);
  };

 const handleSaveAndNext = async () => {
  if (selectedPath !== PATH_OPTIONS.CONNECTOR || !isProjectLinked) {
    toast.error('Please link your project before continuing');
    return;
  }

  if (connectorRequirementFiles.length > 0 && !hasGeneratedRequirements) {
    toast.error('Please generate requirements from your uploaded files before continuing');
    return;
  }

  setIsLoading(true);
  try {
    // No files uploaded - call sync now before navigating
    if (connectorRequirementFiles.length === 0) {
      setShowProgressLoader(true);
      setShouldNavigateAfterComplete(true);
      setCurrentPhase('sync');
      setCurrentProgress(0);
      setProgressMessage('Syncing JIRA epics and stories...');
      clearMessages();
      setIsLoading(false);

      const syncResponse = await axiosInstance.post(
        `/epics/sync-jira-epics/${projectData.project_id}`,
        {}
      );

      if (syncResponse.status_code !== 200 && syncResponse.status_code !== 201) {
        throw new Error('Failed to sync epics');
      }

      sendEvent('sync_epics', {
        project_id: projectData.project_id,
        progress: 0,
        message: "Starting sync..."
      });

      // WebSocket will handle navigation from here
      return;
    }

    // Files already generated - just navigate
    await axiosInstance.post('/wizard/add_wizard_data', {
      wizard_data: {
        engine: state.engine,
        data: state.data
      }
    }).catch(err => {
      console.warn('[Step 4] Warning: Failed to save wizard data:', err.message);
    });

    markStepCompleted(stepId);
    setStepStatus(5, STEP_STATUS.IN_PROGRESS);
    nextStep();
    navigate('/app/onboarding?step=5',{replace:true});
  } catch (error) {
    console.error('Error in save and next:', error);
    toast.error('Failed to continue. Please try again.');
    setShowProgressLoader(false);
    setShouldNavigateAfterComplete(false);
  } finally {
    setIsLoading(false);
  }
};

const handleConfirmProjectLink = async () => {
  setConfirmLoading(true);
  try {
    const sourceParam = connectorType === 'jira' ? 1 : 2;
    const selectedProject = connectorProjects.find(
      (p) => String(p.id) === String(selectedProjects[0])
    );

    if (!selectedProject) {
      toast.error('Selected project not found');
      return;
    }

    const linkResponse = await axiosInstance.post('/projects/link_project_to_source', {
      project_id: projectData.project_id,
      integration_source: sourceParam,
      source_project_id: selectedProject.id,
      source_project_key: selectedProject.key || selectedProject.identifier || ""
    });

    if (linkResponse.status_code !== 200 && linkResponse.status_code !== 201) {
      throw new Error('Failed to link project');
    }

    setTestSource({
      ...testSource,
      pmTool: connectorType,
      pmAccessKey: connectorAccessKey,
      pmApiUrl: connectorApiUrl,
      selectedPMProject: selectedProjects[0],
      linkedProjectKey: selectedProject.key || selectedProject.identifier,
      linkedProjectId: selectedProject.id,
      linkedProjectName: selectedProject.name,
      source_type: SOURCE_TYPES.CONNECTOR,
      connector: {
        system: connectorType,
        selection_ids: selectedProjects,
        meta: {
          accessKey: connectorAccessKey,
          apiUrl: connectorApiUrl,
          projects: [selectedProject]
        }
      }
    });

    setIsProjectLinked(true);
    setShowConfirmModal(false);
    toast.success('Project linked successfully!');

  } catch (error) {
    console.error('Error linking project:', error);
    toast.error(error.response?.data?.detail || 'Failed to link project. Please try again.');
  } finally {
    setConfirmLoading(false);
  }
};

  const handleGenerateRequirements = async () => {
    if (!isProjectLinked) {
      toast.error('Please link your project first');
      return;
    }

    if (connectorRequirementFiles.length === 0) {
      toast.error('Please upload at least one requirements document');
      return;
    }

    const selectedProject = getLinkedProject();
    if (!selectedProject) {
      toast.error('Linked project not found. Please link your project again.');
      return;
    }

    setIsGeneratingRequirements(true);
    hasCalledSync.current = false;
    setShowProgressLoader(true);
    setShouldNavigateAfterComplete(false);
    setCurrentPhase('epic');
    setCurrentProgress(0);
    setProgressMessage('Analyzing requirements and generating epics...');
    clearMessages();

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const email = userData.username || '';

      const formData = new FormData();
      connectorRequirementFiles.forEach((fileObj) => {
        formData.append('files', fileObj.file);
      });
      formData.append('project_key', selectedProject.key || selectedProject.identifier || "");
      formData.append('email', email);

      const generateResponse = await axiosInstance.post(
        '/ai_test_case/generate-epic-tasks/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (generateResponse.status_code !== 200 && generateResponse.status_code !== 201 && !generateResponse.message?.includes('Processing started')) {
        throw new Error('Failed to generate epics');
      }

      await axiosInstance.post('/wizard/add_wizard_data', {
        wizard_data: {
          engine: state.engine,
          data: state.data
        }
      }).catch(err => {
        console.warn('[Step 4] Warning: Failed to save wizard data:', err.message);
      });

      setTestSource({
        ...testSource,
        pmTool: connectorType,
        pmAccessKey: connectorAccessKey,
        pmApiUrl: connectorApiUrl,
        selectedPMProject: selectedProjects[0],
        linkedProjectKey: selectedProject.key || selectedProject.identifier,
        linkedProjectId: selectedProject.id,
        linkedProjectName: selectedProject.name,
        source_type: SOURCE_TYPES.CONNECTOR,
        connector: {
          system: connectorType,
          selection_ids: selectedProjects,
          meta: {
            accessKey: connectorAccessKey,
            apiUrl: connectorApiUrl,
            projects: [selectedProject]
          }
        },
        requirementFileUploaded: true
      });

      sendEvent('sync_epics', {
        project_id: projectData.project_id,
        progress: 0,
        message: "Starting test generation..."
      });

      toast.success('Epic generation started!');
    } catch (fileError) {
      console.error('[Step 4] Error in file upload flow:', fileError);
      setShowProgressLoader(false);
      setIsGeneratingRequirements(false);
      toast.error(fileError.response?.data?.message || 'Failed to generate epics');
    }
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      let step4Output = {};

      // Handle SAMPLE_PROJECT special case
      if (intent === INTENT_TYPES.SAMPLE_PROJECT) {
        console.log('[Step 4] Creating sample project...');
        const response = await createSampleProject();
        step4Output = {
          source_type: SOURCE_TYPES.SAMPLE,
          import_job_id: response.data.import_job_id,
          template_meta: {
            template_id: 'sample_ecommerce',
            captured_fields: {}
          }
        };
        
        markStepCompleted(stepId);
        setStepStatus(5, STEP_STATUS.IN_PROGRESS);
        setTestSource({
          ...testSource,
          step4_output: step4Output,
          selectedPath
        });
        nextStep();
        toast.success('Sample project created successfully');
        navigate('/app/onboarding?step=5',{replace:true});
        return;
      }

      // Handle different paths
      switch (selectedPath) {
        case PATH_OPTIONS.CONNECTOR:
          // Connector path handled by handleSaveAndNext button
          console.log('[Step 4] Using Save & Next for connector path');
          break;

        case PATH_OPTIONS.UPLOAD:
          // TASK 2: Upload Files Flow
          if (uploadFlowRequirementFiles.length === 0 || !isProjectKeySaved) {
            toast.error('Please upload at least one requirements document and save the project key before continuing');
            setIsLoading(false);
            return;
          }

          console.log('[Step 4] Upload path selected - generating epics from file(s)');

          // Show progress loader
          setShowProgressLoader(true);
          setShouldNavigateAfterComplete(true);
          setCurrentPhase('upload');
          setCurrentProgress(0);
          setProgressMessage('Analyzing requirements and generating test Epics and stories...');
          clearMessages();

          try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const email = userData.username || '';

            // Step 1: Call ai_test_case/generate-epic-tasks API with multiple files
            const formData = new FormData();
            
            // Add all files to the FormData
            uploadFlowRequirementFiles.forEach((fileObj) => {
              formData.append('files', fileObj.file);
            });
            
            formData.append('project_id', projectData.project_id);
            formData.append('email', email);

            console.log('[Step 4] Calling generate-epic-tasks API with', uploadFlowRequirementFiles.length, 'file(s)...');
            const generateResponse = await axiosInstance.post(
              '/ai_test_case/generate-epic-tasks/',
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                }
              }
            );

            if (generateResponse.status_code !== 200 && generateResponse.status_code !== 201 && !generateResponse.message?.includes('Processing started')) {
              throw new Error('Failed to generate epics');
            }

            console.log('[Step 4] Epic generation initiated');

            // Step 2: Call wizard/add_wizard_data API for backup
            await axiosInstance.post('/wizard/add_wizard_data', {
        wizard_data: {
          engine: state.engine,
          data: state.data
        }
      }
          ).catch(err => {
              console.warn('[Step 4] Warning: Failed to save wizard data:', err.message);
            });

            // Step 3: Mark steps and update context
            // markStepCompleted(stepId);
            // setStepStatus(5, STEP_STATUS.IN_PROGRESS);
            // // nextStep();

            // Step 4: Trigger WebSocket sync_epics event
            console.log('[Step 4] Triggering WebSocket sync_epics event for upload flow');
            sendEvent('sync_epics', {
              project_id: projectData.project_id,
              progress: 0,
              message: "Starting test generation..."
            });

            // Update test source context
            step4Output = {
              source_type: SOURCE_TYPES.UPLOAD,
              uploads: {
                requirement_files: uploadFlowRequirementFiles.map(f => f.name),
                requirement_file_count: uploadFlowRequirementFiles.length,
                requirement_files_uploaded: true
              }
            };

            setTestSource({
              ...testSource,
              step4_output: step4Output,
              selectedPath
            });

            toast.success('Requirements uploaded! AI is generating test scenarios...');

            // WebSocket progress will handle navigation

          } catch (error) {
            console.error('[Step 4] Error in upload flow:', error);
            setShowProgressLoader(false);
            setShouldNavigateAfterComplete(false);
            toast.error(error.response?.data?.message || 'Failed to process requirements file');
            setIsLoading(false);
          }
          break;

        case PATH_OPTIONS.CREATE:
          // Create in Robitest path
          console.log('[Step 4] Create path selected');
          const reqResponse = await createRequirementsLite({
            template_id: selectedTemplate,
            epics: requirementsLiteData.epics,
            stories: requirementsLiteData.stories
          });

          step4Output = {
            source_type: SOURCE_TYPES.REQUIREMENTS_LITE,
            requirements_lite: {
              epic_ids: reqResponse.data.epic_ids,
              story_ids: reqResponse.data.story_ids
            },
            template_meta: {
              template_id: selectedTemplate,
              captured_fields: {}
            }
          };

          markStepCompleted(stepId);
          setStepStatus(5, STEP_STATUS.IN_PROGRESS);
          setTestSource({
            ...testSource,
            step4_output: step4Output,
            selectedPath
          });
          nextStep();
          toast.success('Source configured successfully');
          navigate('/app/onboarding?step=5',{replace:true});
          break;
      }

    //  markStepCompleted(4);
    // goToStep(5);
    // toast.success('Source configured successfully');
    // navigate('/app/onboarding?step=5');
    } catch (error) {
      console.error('Error processing test source:', error);
      toast.error('Failed to process test source. Please try again.');
      setShowProgressLoader(false);
      setShouldNavigateAfterComplete(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================

  const renderPathSelector = () => {
    if (intent === INTENT_TYPES.SAMPLE_PROJECT) {
      // Don't show path selector for sample project
      return null;
    }

    const pathCards = [
      {
        id: PATH_OPTIONS.CONNECTOR,
        icon: LuLink,
        title: 'Connect a tool',
        description: 'Import epics and stories from JIRA or WorkXiq.',
        disabled: false
      },
      {
        id: PATH_OPTIONS.UPLOAD,
        icon: LuUpload,
        title: 'Upload files',
        description: 'Upload requirement documents in PDF or DOCX format.',
        disabled: false
      },
      {
        id: PATH_OPTIONS.CREATE,
        icon: LuSparkles,
        title: 'Create manually',
        description: 'Write requirements directly in Robitest using templates.',
        disabled: true,
        badge: 'Coming Soon'
      }
    ];

    return (
      <div className="ob-section">
        <div className="ob-section__head">
          <div>
            <h3 className="ob-section__title" id="ob-source-label">Requirements source</h3>
            <p className="ob-section__desc">Choose where your requirements come from.</p>
          </div>
        </div>
        <div className="ob-choice-grid ob-choice-grid--3" role="group" aria-labelledby="ob-source-label">
          {pathCards.map(card => {
            const IconComponent = card.icon;
            const isSelected = selectedPath === card.id && !card.disabled;
            
            return (
              <button
                key={card.id}
                type="button"
                className={`ob-choice ${isSelected ? 'is-selected' : ''} ${card.disabled ? 'is-disabled' : ''}`}
                onClick={() => !card.disabled && setSelectedPath(card.id)}
                disabled={card.disabled}
                aria-pressed={isSelected}
              >
                {isSelected && (
                  <span className="ob-choice__check" aria-hidden="true">
                    <LuCheck size={13} strokeWidth={3} />
                  </span>
                )}
                {card.disabled && card.badge && (
                  <span className="ob-choice__badges">
                    <span className="ob-badge">{card.badge}</span>
                  </span>
                )}
                <span className="ob-choice__icon" aria-hidden="true">
                  <IconComponent size={20} strokeWidth={1.9} />
                </span>
                <span className="ob-choice__body">
                  <span className="ob-choice__title">{card.title}</span>
                  <span className="ob-choice__desc">{card.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const toolName = connectorType === 'jira' ? 'JIRA' : 'WorkXiq';
  const connectorBusy = isGeneratingRequirements || showProgressLoader;

  const renderConnectorPath = () => (
    <div className="wizard-path-content ob-section">
      <div className="ob-section__head">
        <div>
          <h3 className="ob-section__title" id="ob-tool-label">
            Select tool <span className="required">*</span>
          </h3>
          <p className="ob-section__desc">Pick the project management tool that holds your requirements.</p>
        </div>
      </div>
      <div className="ob-choice-grid ob-choice-grid--2" role="group" aria-labelledby="ob-tool-label">
        {[
          { id: 'jira', label: 'JIRA', desc: 'Atlassian Cloud', Icon: LuClipboardList },
          { id: 'project_pulse', label: 'WorkXiq', desc: 'WorkXiq workspace', Icon: LuActivity },
        ].map(({ id, label, desc, Icon }) => {
          const active = connectorType === id;
          return (
            <button
              key={id}
              type="button"
              className={`ob-choice ob-choice--compact ${active ? 'is-selected' : ''}`}
              onClick={() => handleConnectorTypeChange(id)}
              aria-pressed={active}
            >
              {active && (
                <span className="ob-choice__check" aria-hidden="true" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                  <LuCheck size={13} strokeWidth={3} />
                </span>
              )}
              <span className="ob-choice__icon" aria-hidden="true">
                <Icon size={19} />
              </span>
              <span className="ob-choice__body">
                <span className="ob-choice__title">{label}</span>
                <span className="ob-choice__desc">{desc}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Credentials */}
      {connectorType && (
        <>
          <div className="ob-section">
            <div className="ob-section__head">
              <div>
                <h3 className="ob-section__title">Connection details</h3>
                <p className="ob-section__desc">
                  Verify your {toolName} credentials to load the projects you can access.
                </p>
              </div>
              {connectorVerified && <span className="ob-badge ob-badge--success"><LuCheck size={12} /> Verified</span>}
            </div>

            <div className="ob-grid-2" style={{ marginBottom: 0 }}>
              {connectorType === 'jira' && ( 
                <>
                  <div className="wizard-form-group">
                    <label className="wizard-form-label" htmlFor="ob-jira-email">Email <span className="required">*</span></label>
                    <input
                      id="ob-jira-email"
                      type="email"
                      autoComplete="email"
                      className="wizard-form-input"
                      value={jiraEmail}
                      onChange={(e) => setJiraEmail(e.target.value)}
                      placeholder="your.email@company.com"
                      disabled={connectorVerified}
                    />
                  </div>

                  <div className="wizard-form-group">
                    <label className="wizard-form-label" htmlFor="ob-jira-domain">Domain <span className="required">*</span></label>
                    <input
                      id="ob-jira-domain"
                      type="text"
                      className="wizard-form-input"
                      value={jiraDomain}
                      onChange={(e) => setJiraDomain(e.target.value)}
                      placeholder="your-company.atlassian.net"
                      disabled={connectorVerified}
                    />
                  </div>
                </>
              )}

              {connectorType === 'project_pulse' && (
                <div className="wizard-form-group ob-col-span-2">
                  <label className="wizard-form-label" htmlFor="ob-pp-username">Username <span className="required">*</span></label>
                  <input
                    id="ob-pp-username"
                    type="text"
                    autoComplete="username"
                    className="wizard-form-input"
                    value={ppUsername}
                    onChange={(e) => setPpUsername(e.target.value)}
                    placeholder="your.username"
                    disabled={connectorVerified}
                  />
                </div>
              )}

              <div className="wizard-form-group ob-col-span-2">
                <label className="wizard-form-label" htmlFor="ob-connector-token">
                  {connectorType === 'jira' ? 'API Token' : 'App Token'} <span className="required">*</span>
                </label>
                <div className="ob-input-group">
                  <input
                    id="ob-connector-token"
                    type="password"
                    autoComplete="new-password"
                    className="wizard-form-input"
                    value={connectorAccessKey}
                    onChange={(e) => setConnectorAccessKey(e.target.value)}
                    placeholder="Enter token"
                    disabled={connectorVerified}
                    aria-describedby="ob-connector-token-help"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  {!connectorVerified && (
                    <button
                      type="button"
                      className="ob-btn ob-btn--primary"
                      onClick={handleVerifyConnection}
                      disabled={!connectorAccessKey || isLoadingProjects}
                      style={{ height: 'var(--ob-field-h)' }}
                    >
                      {isLoadingProjects ? <span className="ob-spinner" aria-hidden="true" /> : <LuKeyRound size={16} aria-hidden="true" />}
                      {isLoadingProjects ? 'Verifying...' : 'Verify & Load'}
                    </button>
                  )}
                </div>
                <p className="wizard-form-help" id="ob-connector-token-help">
                  {connectorType === 'jira'
                    ? 'Create an API token from your Atlassian account security settings.'
                    : 'Use the app token generated from your WorkXiq account.'}
                </p>
              </div>
            </div>
          </div>

          {/* Project Selection - only when not yet linked */}
          {connectorVerified && !isProjectLinked && (
            <div className="ob-section">
              <div className="wizard-form-group" style={{ marginBottom: 0 }}>
                <label className="wizard-form-label" id="ob-connector-project-label">
                  Select Project <span className="required">*</span>
                </label>
                <div className="ob-input-group">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <WizardSelect
                      aria-labelledby="ob-connector-project-label"
                      value={selectedProjects[0] || ''}
                      onChange={(e) => {
                        setSelectedProjects(e.target.value ? [e.target.value] : []);
                        setIsProjectLinked(false);
                        setHasGeneratedRequirements(false);
                      }}
                    >
                      <option value="">-- Select a project --</option>
                      {connectorProjects.map(project => (
                        <option key={project?.id} value={project?.id}>
                          {project?.key} - {project?.name} 
                        </option>
                      ))}
                    </WizardSelect>
                  </div>
                  {selectedProjects[0] && (
                    <button
                      type="button"
                      className="ob-btn ob-btn--primary"
                      onClick={handleLinkProjectClick}
                      style={{ height: 'var(--ob-field-h)' }}
                    >
                      <LuLink size={16} aria-hidden="true" />
                      Link Project
                    </button>
                  )}
                </div>
                <p className="wizard-form-help">
                  The integration source can&apos;t be changed after the project is linked.
                </p>
              </div>
            </div>
          )}

          {isProjectLinked && (
            <div className="ob-section">
              {renderLinkedProjectCard()}
            </div>
          )}

          {/* Requirement File Upload */}
          {isProjectLinked && (
            <div className="ob-section">
              <div className="ob-section__head">
                <div>
                  <h3 className="ob-section__title">
                    Upload requirements <span className="ob-optional">(Optional)</span>
                  </h3>
                  <p className="ob-section__desc">
                    Add requirement documents to generate epics and stories in your linked project.
                  </p>
                </div>
              </div>
              <FileDropzone
                inputRef={connectorRequirementFileRef}
                onFiles={handleConnectorRequirementFileChange}
                disabled={connectorBusy}
                fileCount={connectorRequirementFiles.length}
                title="Drop requirement documents here"
                hint="PDF or Word documents with requirements, specs or user stories"
                formats={['PDF', 'DOCX']}
              />
              <input
                ref={connectorRequirementFileRef}
                type="file"
                accept=".pdf,.docx"
                multiple
                onChange={(e) => handleConnectorRequirementFileChange(e.target.files)}
                style={{ display: 'none' }}
              />
              <FileList
                title="Selected files"
                files={connectorRequirementFiles}
                onRemove={handleRemoveConnectorRequirementFile}
              />
            </div>
          )}

          {connectorRequirementFiles.length > 0 && !hasGeneratedRequirements && (
            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="ob-btn ob-btn--primary ob-btn--block"
                onClick={handleGenerateRequirements}
                disabled={connectorBusy}
              >
                {connectorBusy ? <span className="ob-spinner" aria-hidden="true" /> : <LuSparkles size={16} aria-hidden="true" />}
                {connectorBusy ? 'Generating...' : 'Generate Requirements'}
              </button>
            </div>
          )}

          {hasGeneratedRequirements && (
            <OnboardingAlert tone="success" title="Requirements generated" style={{ marginTop: 16, marginBottom: 0 }}>
              <p>Requirements generated successfully. You can proceed to the next step.</p>
            </OnboardingAlert>
          )}
        </>
      )}

      <ConfirmIntegrationModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmProjectLink}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={confirmLoading}
        integrationSourceName={connectorType === 'jira' ? 'JIRA' : 'WorkXiq'}
      />
    </div>
  );

  const renderUploadPath = () => (
    <div className="wizard-path-content">
      {/* Requirements Documents - REQUIRED */}
      <div className="ob-section">
        <div className="ob-section__head">
          <div>
            <h3 className="ob-section__title">
              Upload requirements <span className="required">*</span>
            </h3>
            <p className="ob-section__desc">
              Robitest will read these documents to generate epics, stories and test cases.
            </p>
          </div>
        </div>

        <FileDropzone
          inputRef={uploadFlowRequirementFileRef}
          onFiles={handleUploadFlowRequirementFileChange}
          fileCount={uploadFlowRequirementFiles.length}
          title="Drop requirement documents here"
          hint="PDF or Word documents with requirements, specs or user stories"
          formats={['PDF', 'DOCX']}
        />
        
        <input
          ref={uploadFlowRequirementFileRef}
          type="file"
          accept=".pdf,.docx"
          multiple
          onChange={(e) => handleUploadFlowRequirementFileChange(e.target.files)}
          style={{ display: 'none' }}
        />
        
        <FileList
          title="Selected files"
          files={uploadFlowRequirementFiles}
          onRemove={handleRemoveUploadFlowRequirementFile}
        />

        <OnboardingAlert tone="info" style={{ marginTop: 16, marginBottom: 0 }}>
          <p>You can import these requirements into your project management tool (JIRA / WorkXiq) later.</p>
        </OnboardingAlert>
      </div>

      <div className="ob-section ob-project-key">
        <ProjectKeyField
          value={uploadProjectKey}
          setUploadProjectKey={setUploadProjectKey}
          onChange={setUploadProjectKey}
          onErrorChange={setUploadProjectKeyError}
          error={uploadProjectKeyError}
          projectId={projectData.project_id}
          onKeySaved={setIsProjectKeySaved} 
          projectName={projectNameFromStorage}
        />
      </div>
    </div>
  );

  const renderCreatePath = () => (
    <div className="wizard-path-content">
      {/* Template Selection */}
      <div className="wizard-form-group">
        <label className="wizard-form-label">Choose Template <span className="required">*</span></label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {TEMPLATE_OPTIONS.map(template => {
            const TemplateIcon = template.icon;
            return (
              <button
                key={template.id}
                type="button"
                className={`wizard-template-card ${selectedTemplate === template.id ? 'active' : ''}`}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <div style={{ marginBottom: '0.75rem', color: '#4361ee', display: 'flex', justifyContent: 'center' }}>
                  <TemplateIcon size={32} strokeWidth={1.5} />
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{template.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{template.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Requirements Lite Editor */}
      {selectedTemplate && selectedTemplate !== 'custom' && (
        <>
          <div className="wizard-form-group">
            <label className="wizard-form-label">Quick Add Requirements</label>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <button
                type="button"
                className={`wizard-btn-sm ${inlineRequirement.type === 'epic' ? 'wizard-btn-primary' : 'wizard-btn-secondary'}`}
                onClick={() => setInlineRequirement(prev => ({ ...prev, type: 'epic' }))}
              >
                Epic
              </button>
              <button
                type="button"
                className={`wizard-btn-sm ${inlineRequirement.type === 'story' ? 'wizard-btn-primary' : 'wizard-btn-secondary'}`}
                onClick={() => setInlineRequirement(prev => ({ ...prev, type: 'story' }))}
              >
                Story
              </button>
            </div>

            <input
              type="text"
              className="wizard-form-input"
              placeholder="Title"
              value={inlineRequirement.title}
              onChange={(e) => setInlineRequirement(prev => ({ ...prev, title: e.target.value }))}
              style={{ marginBottom: '0.5rem' }}
            />
            
            <textarea
              className="wizard-form-textarea"
              placeholder="Description (optional)"
              value={inlineRequirement.description}
              onChange={(e) => setInlineRequirement(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              style={{ marginBottom: '0.5rem' }}
            />

            <button
              type="button"
              className="wizard-btn wizard-btn-secondary"
              onClick={handleAddRequirement}
              style={{ width: '100%' }}
            >
              + Add {inlineRequirement.type === 'epic' ? 'Epic' : 'Story'}
            </button>
          </div>

          {/* Show added requirements */}
          {(requirementsLiteData.epics.length > 0 || requirementsLiteData.stories.length > 0) && (
            <div className="wizard-form-group">
              <label className="wizard-form-label">Added Requirements</label>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {requirementsLiteData.epics.map(epic => (
                  <div key={epic.id} style={{ 
                    padding: '0.75rem', 
                    background: 'var(--bg-gray-50)', 
                    borderRadius: '6px', 
                    marginBottom: '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                        <span style={{ color: 'var(--primary-color)', marginRight: '0.5rem' }}>📑</span>
                        {epic.title}
                      </div>
                      {epic.description && (
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {epic.description}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveRequirement('epic', epic.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger-color)' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                
                {requirementsLiteData.stories.map(story => (
                  <div key={story.id} style={{ 
                    padding: '0.75rem', 
                    background: 'var(--bg-gray-50)', 
                    borderRadius: '6px', 
                    marginBottom: '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                        <span style={{ color: 'var(--secondary-color)', marginRight: '0.5rem' }}>📝</span>
                        {story.title}
                      </div>
                      {story.description && (
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {story.description}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveRequirement('story', story.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger-color)' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <p className="wizard-form-help">
                {requirementsLiteData.epics.length} epic(s), {requirementsLiteData.stories.length} story/stories
              </p>
            </div>
          )}
        </>
      )}

      {selectedTemplate === 'custom' && (
        <div style={{ 
          background: 'rgba(59, 130, 246, 0.1)', 
          padding: '1.5rem', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✨</div>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Custom template selected. You'll be able to define your requirements<br />from scratch in the next step.
          </p>
        </div>
      )}
    </div>
  );

  const renderSampleProjectContent = () => (
    <div style={{ 
      background: 'rgba(76, 201, 240, 0.1)', 
      padding: '2rem', 
      borderRadius: '12px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎯</div>
      <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>
        Sample E-Commerce Project Ready
      </h3>
      <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
        We will create a complete demo project with realistic test scenarios:<br/>
        • User Authentication (Login, Register, Logout)<br/>
        • Product Catalog (Search, Filter, Details)<br/>
        • Shopping Cart (Add, Update, Remove Items)<br/>
        • Checkout Flow (Address, Payment, Confirmation)
      </p>
      <div style={{ 
        marginTop: '1.5rem', 
        padding: '1rem', 
        background: 'white', 
        borderRadius: '8px',
        border: '1px solid var(--border-color)'
      }}>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
          Includes:
        </p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
          fontSize: '0.875rem'
        }}>
          <span>✓ 15 Test Cases</span>
          <span>✓ 3 Test Suites</span>
          <span>✓ Sample Data</span>
          <span>✓ Ready to Run</span>
        </div>
      </div>
    </div>
  );

  const renderPathContent = () => {
    if (intent === INTENT_TYPES.SAMPLE_PROJECT) {
      return renderSampleProjectContent();
    }

    switch (selectedPath) {
      case PATH_OPTIONS.CONNECTOR:
        return renderConnectorPath();
      case PATH_OPTIONS.UPLOAD:
        return renderUploadPath();
      case PATH_OPTIONS.CREATE:
        return renderCreatePath();
      default:
        return null;
    }
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={4} completedSteps={engine.flow.completedSteps} />
        
        <WizardCard
          title={intent === INTENT_TYPES.SAMPLE_PROJECT ? "Sample Project" : "Add your requirements"}
          subtitle={
            intent === INTENT_TYPES.SAMPLE_PROJECT
              ? "We'll set up a sample project for you"
              : "Choose how Robitest should receive the requirements used to generate your tests."
          }
        >
          {isAnalyzingRequirement && (
            <OnboardingModal
              size="sm"
              align="center"
              dismissible={false}
              busy
              icon={<span className="ob-spinner ob-spinner--lg" aria-hidden="true" />}
              title="AI is analyzing your work items"
              description="Please wait while we process your document..."
            />
          )}

          {/* Progress Loader with WebSocket Updates */}
          {showProgressLoader && (
            <OnboardingModal
              align="center"
              dismissible={false}
              busy
              icon={<LuSparkles size={20} />}
              title={progressMessage}
              description={
                currentPhase === 'epic'
                  ? 'Analyzing your requirements and generating epics...'
                  : currentPhase === 'sync'
                  ? 'Syncing with your project management tool...'
                  : currentPhase === 'upload'
                  ? 'Processing requirements and generating test Tasks...'
                  : 'This may take a few moments...'
              }
            >
              <div style={{ textAlign: 'left' }}>
                <div
                  className="ob-progress"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={wsProgress}
                  aria-label="Processing progress"
                >
                  <div className="ob-progress__fill" style={{ width: `${wsProgress}%` }} />
                </div>
                <div className="ob-progress-meta">
                  <span>
                    {currentPhase === 'epic' && 'Generating work items'}
                    {currentPhase === 'sync' && 'Syncing epics'}
                    {currentPhase === 'upload' && 'Generating Epics and Stories'}
                    {['epic', 'sync', 'upload'].includes(currentPhase) === false && 'Processing'}
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{wsProgress}%</span>
                </div>
                <ul ref={wsMessagesContainerRef} className="ob-status-list" aria-live="polite" style={{ marginBottom: 24 }}>
                  {displayWsMessages.length === 0
                    ? <li style={{ justifyContent: 'center', color: 'var(--text-muted)' }}>Waiting for updates...</li>
                    : displayWsMessages.map((text, i) => (
                      <li key={`${i}-${text.slice(0, 24)}`}>
                        <LuCheck size={13} aria-hidden="true" />
                        <span style={{ wordBreak: 'break-word' }}>{text}</span>
                      </li>
                    ))
                  }
                </ul>
              </div>
            </OnboardingModal>
          )}
 
          {renderPathSelector()}
          {renderPathContent()}

          <WizardActions
            showBack={!isCompleted && !showProgressLoader}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=3');
            }}
            showNext={true}
            onNext={isCreating && selectedPath === PATH_OPTIONS.CONNECTOR ? handleSaveAndNext : handleContinue}
            nextLabel={
              intent === INTENT_TYPES.SAMPLE_PROJECT 
                ? 'Create Sample Project' 
                : (isCreating ? 'Save & Next' : 'Continue')
            }
            nextLoading={isLoading || confirmLoading || isAnalyzingRequirement || showProgressLoader || isGeneratingRequirements}
            // nextDisabled={showProgressLoader}
            nextDisabled={!canContinue() || isAnalyzingRequirement || showProgressLoader}

          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step4TestSource;
